/**
 * Session 18 — derive `NumberOfConnectors` from the live topology.
 *
 * The configuration tables for v1.6 and v2.0.1 both expose a
 * `NumberOfConnectors` variable that the CSMS uses to size GUIs and
 * tx-id ranges. Hardcoding a sentinel "3" (the original default in
 * `OcppV16Adapter.DEFAULT_CONFIGURATION_KEYS_V16` and
 * `v201/variables.ts`) drifts as soon as the user edits the topology
 * — three EVSEs with 1, 1, 2 connectors really should show as 4, not
 * the seeded 3.
 *
 * This module is the single source of truth for:
 *   - the key NAME per OCPP version (1.6 = flat, 2.0.1 = namespaced
 *     under DeviceDataCtrlr);
 *   - the COUNT computation (sum of `connectorIds.length` across
 *     all EVSEs in the topology).
 *
 * The configuration store seed path overrides the descriptor default
 * with this value; `useStationStore.setTopology` also writes the
 * updated count so a CSMS-initiated `GetVariables` /
 * `GetConfiguration` reply stays in sync without a UI round-trip.
 */
/**
 * Lib must stay a leaf (layers.yaml), so the OCPP version is typed as
 * a literal string union here rather than imported from the domain
 * `OcppVersion` enum. Callers pass `station.ocppVersion` directly —
 * the enum's string values match this union exactly.
 */
export type OcppVersionString = '1.6J' | '2.0.1' | '2.1';

export interface EvseLike {
  readonly connectorIds: readonly unknown[];
}

export function connectorCountFor(evses: readonly EvseLike[]): number {
  let total = 0;
  for (const e of evses) total += e.connectorIds.length;
  return total;
}

export function numberOfConnectorsKey(ocppVersion: OcppVersionString): string {
  return ocppVersion === '2.0.1'
    ? 'DeviceDataCtrlr.NumberOfConnectors'
    : 'NumberOfConnectors';
}
